import React, { useEffect, useState, useCallback } from "react";
import {
  InputBase,
  FormControl,
  FormHelperText,
  InputAdornment,
  Divider,
  IconButton,
} from "@material-ui/core";
import * as Icons from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import MuiButton from "Components/MuiButton/MuiButton";
import ToggleButton from "./components/toggleButton";
import CheckValidity from "Helpers/CheckValidity";
import showNotify from "Helpers/Notify";
import { PriceShow } from "Helpers/PriceShow";
import { convertToEnglishNumber } from "Helpers/convertToEnglishNumber";
import { useSelector } from "react-redux";
import { useTheme } from "@material-ui/core/styles";
import "Styles/components/_input.scss";

function Input({
  setValue,
  value,
  name,
  label,
  validation,
  helperText = "",
  type,
  icon,
  endIcon = "",
  readOnly,
  checkValid,
  multiline,
  onSearch,
  onClearSearch,
  onDisableKeyboard = false,
  longLabel,
  radioGroup,
  ...props
}) {
  const { t } = useTranslation();
  const { direction } = useTheme();
  const { precision } = useSelector((state) => state.settings);

  const [helperTxt, setHelperTxt] = useState(helperText);
  const [errStyle, setErrStyle] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [handleType, setHandleType] = useState(type);
  const [valParams, setValParams] = useState({});
  const isPercent = endIcon === "%";

  useEffect(() => {
    setHandleType(handleType || "text");
  }, [type]);

  const validate = useCallback(
    (val) => {
      if (!validation) return { isValid: true };
      const result = CheckValidity(
        val,
        validation,
        props.placeholder,
        precision ?? 0,
        isPercent,
      );
      if (result.isValid) {
        setErrStyle(false);
        setHelperTxt(helperText || "");
      } else {
        setErrStyle(true);
        setHelperTxt(result.errText[0]);

        if (result.errText[0]?.includes("WID")) {
          setValParams(result.params || {});
        }
      }
      return result;
    },
    [validation, helperText, precision, isPercent],
  );

  const onInputChange = (e) => {
    const raw = convertToEnglishNumber(e.target.value);
    const result = validate(raw);
    setValue(name, raw, result.isValid);
  };

  const togglePassword = () => {
    setShowPassword((p) => !p);
    setHandleType((p) => (p === "password" ? "text" : "password"));
  };

  const onKeyUp = (event) => {
    if (type === "search") {
      if (onDisableKeyboard) {
        if (event.key === "Enter" || event.key === "Escape") {
          showNotify({
            text: t("useSearchBtn"),
            customClass: "error",
          });
        }
      } else {
        if (event.key === "Enter") onSearch?.(event);
        if (event.key === "Escape") onClearSearch?.(event);
      }
    }
  };

  const startAdornment = (() => {
    if (handleType === "search" && value !== "") {
      return (
        <InputAdornment position="start">
          {
            <Icons.XLg
              className="cancel-search-icon handC"
              onClick={onClearSearch}
              size={30}
              color="red"
            />
          }
        </InputAdornment>
      );
    } else if (icon) {
      return <InputAdornment position="start">{icon}</InputAdornment>;
    }

    return null;
  })();

  const endAdornment = (() => {
    if (handleType === "password") {
      return (
        <InputAdornment>
          <IconButton onClick={togglePassword}>
            {showPassword ? <Icons.Eye /> : <Icons.EyeSlash />}
          </IconButton>
        </InputAdornment>
      );
    }

    if (handleType === "search") {
      return (
        <MuiButton
          color="primary"
          className="input-searchB-button"
          innerIcon={<Icons.Search />}
          onClick={onSearch}
          disabled={readOnly || props.disabled}
        />
      );
    }

    if (radioGroup) {
      return (
        <>
          {endIcon && (
            <span className="adornedEnd">
              {endIcon === "%" ? <Icons.Percent /> : endIcon}
            </span>
          )}
          <Divider orientation="vertical" flexItem />
          <ToggleButton props={radioGroup} />
        </>
      );
    }

    if (endIcon) {
      return (
        <span className="adornedEnd">
          {endIcon === "%" ? <Icons.Percent /> : endIcon}
        </span>
      );
    }

    return null;
  })();

  const displayValue =
    handleType === "price" || isPercent
      ? PriceShow(value, precision, isPercent)
      : value || "";

  return (
    <FormControl
      error={errStyle}
      className={`form-input ${longLabel ? "longLabel" : ""}`}
    >
      {type !== "search" && (
        <label
          className={`input-label ${validation?.required ? "required" : ""}`}
          htmlFor={name}
          style={{ textAlign: direction === "rtl" ? "right" : "left" }}
        >
          {label}
        </label>
      )}

      <InputBase
        id={name}
        value={displayValue}
        onChange={onInputChange}
        onKeyUp={onKeyUp}
        multiline={!!multiline}
        maxRows={multiline ? 3 : 0}
        readOnly={readOnly}
        disabled={props.disabled}
        placeholder={props.placeholder}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        type={
          ["number", "millimeter", "price"].includes(handleType)
            ? "text"
            : handleType
        }
        inputProps={{
          inputMode: ["number", "millimeter", "price"].includes(handleType)
            ? "decimal"
            : undefined,
          ...props,
        }}
      />

      {!multiline && type !== "search" && (
        <FormHelperText>
          {helperTxt.includes("WID")
            ? t(helperTxt).replace(
                "@S1",
                typeof valParams.S1 === "number"
                  ? valParams.S1.toLocaleString()
                  : valParams.S1,
              )
            : t(helperTxt)}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default Input;